import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import VerifyIdFlow from "@/components/verify/VerifyIdFlow";
import { CheckCircle2, Loader2, ArrowRight, CreditCard, LifeBuoy, RotateCcw, ShieldAlert } from "lucide-react";

// Return landing after Stripe. CRITICAL: the redirect carries no trustworthy verdict — we read
// the REAL VerificationSession status from our backend and render one of three states. Success is
// NEVER shown on redirect alone.
const FAIL_COPY = {
  _default: { title: "We couldn't read that ID", body: "The photo was too blurry to match. This happens a lot — try again in brighter light, and hold steady until the shutter fires." },
  document_expired: { title: "That ID looks expired", body: "The document appears to be expired. Try again with a current, unexpired government ID." },
  document_type_not_supported: { title: "That document type isn't supported", body: "Use a driver's license, state ID, or passport, and try again." },
  selfie_face_mismatch: { title: "The selfie didn't match", body: "The selfie couldn't be matched to the document photo. Retake in even lighting, facing the camera straight on." },
  selfie_unverified_other: { title: "The selfie didn't match", body: "The selfie couldn't be matched to the document photo. Retake in even lighting, facing the camera straight on." },
  consent_declined: { title: "Verification wasn't completed", body: "It looks like the check was cancelled before it finished. You can start again whenever you're ready." },
};

export default function VerifyComplete() {
  const [params] = useSearchParams();
  const next = params.get("next") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retry, setRetry] = useState(false);
  const stopped = useRef(false);
  const tries = useRef(0);

  const safeNext = next.startsWith("/") && !next.startsWith("//") ? next : "/dashboard";

  const poll = useCallback(async () => {
    if (stopped.current) return;
    try {
      const { data: d } = await api.get("/identity/status");
      setData(d);
      setLoading(false);
      if (d.verified) { refresh(); return; }
      if (d.status === "requires_input" || d.locked) return; // terminal for this attempt
    } catch { /* keep trying */ }
    tries.current += 1;
    if (tries.current < 16 && !stopped.current) setTimeout(poll, 2500);
    else setLoading(false);
  }, [refresh]);

  useEffect(() => { poll(); return () => { stopped.current = true; }; }, [poll]);

  const state = !data ? "processing"
    : data.verified ? "verified"
    : data.locked ? "locked"
    : data.status === "requires_input" ? "failed"
    : "processing";

  const fail = FAIL_COPY[data?.error_code] || FAIL_COPY._default;
  const attempt = Math.min(data?.attempts || 1, data?.max_attempts || 3);

  const Pill = ({ cls, label }) => (
    <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border ${cls}`} data-testid="verify-pill">
      <span className="text-[8px] leading-none">●</span> {label}
    </span>
  );
  const btnPrimary = "inline-flex items-center justify-center gap-2 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm px-5 py-3 transition-colors";
  const btnGhost = "inline-flex items-center justify-center gap-2 border border-sniper text-secondary-foreground hover:text-foreground font-mono uppercase text-xs px-5 py-3 transition-colors";

  return (
    <div className="min-h-screen text-foreground" data-testid="verify-complete-page">
      <Navbar />
      <div className="max-w-md mx-auto px-4 py-14">
        <div className="bg-surface border border-sniper p-7">
          {loading && !data ? (
            <p className="font-mono text-xs uppercase text-muted-foreground inline-flex items-center gap-2" data-testid="verify-complete-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Checking your result…
            </p>
          ) : state === "processing" ? (
            <div data-testid="verify-state-processing">
              <Pill cls="text-[#d3a24e] border-[#d3a24e]/40 bg-[#d3a24e]/10" label="Still checking" />
              <h1 className="font-heading text-3xl uppercase tracking-tight mt-4">We're reviewing your ID</h1>
              <p className="text-sm font-body text-secondary-foreground mt-3 leading-relaxed">
                Usually under a minute, occasionally a few hours. You can keep using OpticSwap while it runs —
                we'll email you the moment it clears.
              </p>
              <button onClick={() => navigate("/feed")} className={`${btnGhost} mt-6`} data-testid="verify-back-marketplace">
                Back to marketplace
              </button>
            </div>
          ) : state === "verified" ? (
            <div data-testid="verify-state-verified">
              <Pill cls="text-volt border-olive-bright/50 bg-olive/20" label="Verified" />
              <h1 className="font-heading text-3xl uppercase tracking-tight mt-4 flex items-center gap-2">
                <CheckCircle2 className="w-7 h-7 text-volt" /> You're verified
              </h1>
              <p className="text-sm font-body text-secondary-foreground mt-3 leading-relaxed">
                Your ID VERIFIED badge is live on your profile and listings. Next: add a payout method so your
                first sale can settle.
              </p>
              <div className="mt-6 flex flex-col gap-2">
                <button onClick={() => navigate("/settings/payouts")} className={btnPrimary} data-testid="verify-setup-payouts">
                  <CreditCard className="w-4 h-4" /> Set up payouts <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => navigate(safeNext)} className={btnGhost} data-testid="verify-not-now">Not now</button>
              </div>
            </div>
          ) : state === "locked" ? (
            <div data-testid="verify-state-locked">
              <Pill cls="text-[#E39C8A] border-[#E39C8A]/40 bg-[#48180F]" label={`Attempt ${data.max_attempts} of ${data.max_attempts}`} />
              <h1 className="font-heading text-3xl uppercase tracking-tight mt-4 flex items-center gap-2">
                <ShieldAlert className="w-7 h-7 text-[#E39C8A]" /> Handed to our team
              </h1>
              <p className="text-sm font-body text-secondary-foreground mt-3 leading-relaxed">
                You've used all {data.max_attempts} attempts, so an admin is reviewing your verification manually —
                you're not locked out of your account. We'll email you when it's sorted.
              </p>
              <div className="mt-6 flex flex-col gap-2">
                <button onClick={() => navigate("/feed")} className={btnPrimary} data-testid="verify-back-marketplace">
                  Back to marketplace
                </button>
                <button onClick={openHelp} className={btnGhost} data-testid="verify-get-help">
                  <LifeBuoy className="w-4 h-4" /> Get help
                </button>
              </div>
            </div>
          ) : (
            /* failed */
            <div data-testid="verify-state-failed">
              <Pill cls="text-[#E39C8A] border-[#E39C8A]/40 bg-[#48180F]" label={`Attempt ${attempt} of ${data.max_attempts || 3}`} />
              <h1 className="font-heading text-3xl uppercase tracking-tight mt-4">{fail.title}</h1>
              <p className="text-sm font-body text-secondary-foreground mt-3 leading-relaxed">{fail.body}</p>
              <div className="mt-6 flex flex-col gap-2">
                <button onClick={() => setRetry(true)} className={btnPrimary} data-testid="verify-try-again">
                  <RotateCcw className="w-4 h-4" /> Try again <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={openHelp} className={btnGhost} data-testid="verify-get-help">
                  <LifeBuoy className="w-4 h-4" /> Get help
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <VerifyIdFlow open={retry} onClose={() => setRetry(false)} next={safeNext} />
    </div>
  );
}

function openHelp() {
  import("@/components/FeedbackModal").then((m) => m.openFeedback({ category: "Other", prefill: "[Verification] " }));
}
